import { ArrowDown, ArrowUp, Info, Minus } from 'lucide-react'
import { ResponsiveContainer, LineChart, Line } from 'recharts'
import { Card, CardContent } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { MetricPair, IntMetricPair } from '@/types'
import { MetricLabel } from '@/components/finance/MetricLabel'
import type { MetricKey } from '@/lib/financeGlossary'

interface MetricTileProps {
  label: string
  /**
   * Optional glossary key. When present we render the label as a
   * MetricLabel so the user gets a tooltip with the canonical formula
   * and source. Tiles with no equivalent glossary entry can omit it.
   */
  metricKey?: MetricKey
  metric: MetricPair | IntMetricPair
  /** Renderer for the *current* value (e.g. formatCurrency or formatNumber). */
  formatValue: (value: number) => string
  /** Renderer for the *previous* value shown in the comparison label. */
  formatPrevious?: (value: number) => string
  /** Optional human label for the comparison window (e.g. "vs. last 7 days"). */
  comparisonLabel: string
  icon?: React.ReactNode
  /** When true, downward deltas read as positive (e.g. for "Discounts"). */
  invertColors?: boolean
  /** Optional sub-label rendered between the headline and the delta. Used by
   *  the Projected Gross Margin tile to show the coverage % so the user
   *  knows what fraction of net sales the metric was computed from. */
  subtitle?: React.ReactNode
  /** Match Revenue &amp; profit / dashboard: stronger border + tinted fill. */
  emphasised?: boolean
  /** Optional per-day series rendered as a tiny axis-less sparkline in the
   *  bottom-right of the tile. Used by the Marketing Overview KPI strip so
   *  owners can spot a trend without leaving the tile. Pass undefined to
   *  hide; pass an empty array to hide cleanly when no data is available. */
  sparkline?: number[]
  /** Inline tooltip copy for non-finance tiles (e.g. Marketing Overview).
   *  When present, the label gets the same (i) icon + dotted-underline
   *  affordance as MetricLabel renders for finance metrics, but the copy
   *  is supplied directly instead of looked up in the glossary. */
  tooltip?: { label?: string; summary: string }
}

/**
 * KPI tile with a real period-over-period delta. Replaces the hardcoded
 * "+12.5%" Growth Rate card from the legacy reports page.
 *
 * Color semantics:
 *   - Up vs. previous: green (good for revenue/orders, can be inverted for
 *     discount-style metrics where a decrease is the win).
 *   - Down: red.
 *   - Equal: neutral.
 *   - Previous = 0 (delta has no sensible percentage): we render a single
 *     baseline label "no comparison" rather than misleading +∞%.
 */
export function MetricTile({
  label,
  metricKey,
  metric,
  formatValue,
  formatPrevious,
  comparisonLabel,
  icon,
  invertColors = false,
  subtitle,
  emphasised = false,
  sparkline,
  tooltip,
}: MetricTileProps) {
  const formatPrev = formatPrevious ?? formatValue
  const hasPct = metric.pct != null
  const isUp = metric.delta > 0
  const isFlat = metric.delta === 0

  let toneClass = 'text-muted-foreground'
  let DirectionIcon = Minus
  if (!isFlat) {
    DirectionIcon = isUp ? ArrowUp : ArrowDown
    const goodWhenUp = !invertColors
    const isGood = (isUp && goodWhenUp) || (!isUp && !goodWhenUp)
    toneClass = isGood ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
  }

  const pctText = hasPct
    ? `${metric.pct! > 0 ? '+' : ''}${metric.pct!.toFixed(1)}%`
    : '—'

  // Only render the sparkline when we have at least two non-zero points
  // — a flat line of zeros adds noise without signal.
  const sparkData = sparkline && sparkline.length >= 2 && sparkline.some(v => v > 0)
    ? sparkline.map((v, i) => ({ i, v }))
    : null
  const goodWhenUp = !invertColors
  const sparkStrokeClass = isFlat
    ? 'text-muted-foreground/60'
    : ((isUp && goodWhenUp) || (!isUp && !goodWhenUp))
      ? 'text-emerald-500/80'
      : 'text-red-500/80'

  return (
    <Card className={cn(emphasised && 'border-primary/30 bg-primary/5')}>
      <CardContent className="p-5 space-y-2">
        <div className="flex items-center justify-between">
          {metricKey ? (
            <MetricLabel
              metric={metricKey}
              label={label}
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            />
          ) : tooltip ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 cursor-help underline-offset-2 decoration-dotted hover:underline text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <span>{label}</span>
                    <Info className="h-3 w-3 text-muted-foreground/70" aria-hidden />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-left">
                  <div className="space-y-1">
                    <div className="text-sm font-semibold leading-tight text-popover-foreground">
                      {tooltip.label ?? label}
                    </div>
                    <p className="text-xs leading-relaxed text-popover-foreground/90">
                      {tooltip.summary}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          )}
          {icon ? <span className="text-muted-foreground">{icon}</span> : null}
        </div>
        <div className="flex items-end justify-between gap-3">
          <p className={cn('text-2xl font-semibold tabular-nums', emphasised && 'text-primary')}>
            {formatValue(metric.current)}
          </p>
          {sparkData && (
            <div className={cn('h-8 w-20 shrink-0', sparkStrokeClass)} aria-hidden>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sparkData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                  <Line
                    type="monotone"
                    dataKey="v"
                    stroke="currentColor"
                    strokeWidth={1.75}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-md bg-muted',
              toneClass,
            )}
          >
            <DirectionIcon className="w-3 h-3" />
            {pctText}
          </span>
          <span className="text-xs text-muted-foreground">
            {hasPct ? `${comparisonLabel} (${formatPrev(metric.previous)})` : comparisonLabel}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
